const RATE_LIMIT_STORAGE_KEY = 'rateLimits';

/**
 * Get rate limits from localStorage
 * @returns {Object} Rate limits object
 */
const getRateLimitsFromStorage = () => {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to parse rate limits from localStorage:', error);
    return {};
  }
};

/**
 * Save rate limits to localStorage
 * @param {Object} rateLimits - Rate limits object to save
 */
const saveRateLimitsToStorage = (rateLimits) => {
  try {
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(rateLimits));
  } catch (error) {
    console.warn('Failed to save rate limits to localStorage:', error);
  }
};

/**
 * Clean up expired entries from localStorage to prevent bloat
 * @param {Object} rateLimits - Rate limits object
 * @param {number} maxAge - Maximum age in milliseconds (default: 24 hours)
 * @returns {Object} Cleaned rate limits object
 */
const cleanupExpiredEntries = (rateLimits, maxAge = 24 * 60 * 60 * 1000) => {
  const now = Date.now();
  const cleaned = {};
  
  Object.keys(rateLimits).forEach(key => {
    const timestamps = rateLimits[key];
    const validTimestamps = timestamps.filter(ts => now - ts < maxAge);
    
    // Only keep entries that have recent timestamps
    if (validTimestamps.length > 0) {
      cleaned[key] = validTimestamps;
    }
  });
  
  return cleaned;
};

/**
 * General-purpose rate limiter with localStorage persistence.
 * @param {string} key - Unique identifier (e.g. userId:gameId:action)
 * @param {number} limit - Maximum allowed actions
 * @param {number} windowMs - Time window in milliseconds
 * @returns {boolean} - true if rate limit exceeded, false otherwise
 */
export const checkRateLimit = ({ key, limit, windowMs }) => {
  const now = Date.now();
  
  // Get current rate limits from localStorage
  let rateLimits = getRateLimitsFromStorage();
  
  // Clean up expired entries periodically (every 100th call)
  if (Math.random() < 0.01) {
    rateLimits = cleanupExpiredEntries(rateLimits);
  }
  
  // Get attempts for this specific key
  const attempts = rateLimits[key] || [];
  
  // Filter to only recent attempts within the time window
  const recent = attempts.filter(ts => now - ts < windowMs);

  // Check if limit is exceeded
  if (recent.length >= limit) {
    return true;
  }

  // Add current attempt and save back to localStorage
  recent.push(now);
  rateLimits[key] = recent;
  saveRateLimitsToStorage(rateLimits);
  
  return false;
};

/**
 * Clear all rate limits from localStorage (useful for development/testing)
 */
export const clearAllRateLimits = () => {
  try {
    localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
    // console.log('All rate limits cleared');
  } catch (error) {
    console.warn('Failed to clear rate limits:', error);
  }
};

/**
 * Clear rate limits for a specific key
 * @param {string} key - The rate limit key to clear
 */
export const clearRateLimit = (key) => {
  try {
    const rateLimits = getRateLimitsFromStorage();
    delete rateLimits[key];
    saveRateLimitsToStorage(rateLimits);
    // console.log(`Rate limit cleared for key: ${key}`);
  } catch (error) {
    console.warn('Failed to clear rate limit for key:', key, error);
  }
}; 